import { useEffect, useState } from 'react';

import EventTypeTitle from './EventTypeTitle';
import MarkdownEditor from './partials/MarkdownEditor';
import { GAME_HOST_UNLOCK_PRODUCT_ID, LEVYRAATI_HOST_UNLOCK_PRODUCT_ID } from './partials/profileHelpers';
import { useCreditProductUnlocked } from '../useStatisticsUnlock';
import { VIRTUAL_ROOM_COUNT } from '../config/virtualRooms';
import { LIVESTREAM_GENRES, LIVESTREAM_LANGUAGES } from '../config/livestreamConstants';
import { useT } from '../i18n/I18nModule';

const API = process.env.REACT_APP_API_URL || '';
const DAY_START = 7 * 60;
const DAY_END = 23 * 60;

function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [hh, mm = 0] = timeStr.split(':').map((x) => parseInt(x, 10) || 0);
  return hh * 60 + mm;
}

function isSpecificSlotFree(eventsOnDay, startTimeStr, durationMinutes, roomNumber = 1) {
  const start = timeToMinutes(startTimeStr);
  const end = start + durationMinutes;
  if (start < DAY_START || end > DAY_END) return false;
  const occupied = eventsOnDay
    .filter((ev) => (ev.roomNumber ?? ev.room_number ?? 1) === roomNumber && ev.time != null)
    .map((ev) => ({
      start: timeToMinutes(ev.time),
      end: timeToMinutes(ev.time) + (ev.minutes || 0),
    }));
  for (const block of occupied) {
    if (start < block.end && end > block.start) return false;
  }
  return true;
}

function todayStr() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function isDateAndTimeInFuture(eventDate, eventTime, durationMinutes) {
  if (!eventDate || !eventTime) return false;
  const today = todayStr();
  if (eventDate > today) return true;
  if (eventDate < today) return false;
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const startMinutes = timeToMinutes(eventTime);
  return startMinutes + durationMinutes > nowMinutes;
}

export default function CreateEventView({ user, onCancel, onSaved }) {
  const t = useT();
  const [eventName, setEventName] = useState('');
  const [durationHours, setDurationHours] = useState(2);
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('09:00');
  const [eventType, setEventType] = useState('keskustelu'); // 'keskustelu' | 'game' | 'levyraati' | 'movie'
  const [embedUrl, setEmbedUrl] = useState('');
  const [description, setDescription] = useState('');
  const [enableQA, setEnableQA] = useState(false);
  const [eventsOnDay, setEventsOnDay] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [gameType, setGameType] = useState('riddle-fi');
  const [levyraatiPlaylist, setLevyraatiPlaylist] = useState([]);
  const [levyraatiTrackUrl, setLevyraatiTrackUrl] = useState('');
  const [levyraatiResolving, setLevyraatiResolving] = useState(false);
  const [levyraatiResolveError, setLevyraatiResolveError] = useState(null);
  const [levyraatiResolveHint, setLevyraatiResolveHint] = useState(null);
  const [levyraatiRandomLoading, setLevyraatiRandomLoading] = useState(false);
  const [movieId, setMovieId] = useState('');
  const [movies, setMovies] = useState([]);
  const [roomNumber, setRoomNumber] = useState(1);
  const [livestreamType, setLivestreamType] = useState('music');
  const [livestreamCategory, setLivestreamCategory] = useState('pop');

  const { loading: gameHostUnlockLoading, unlocked: gameHostUnlocked } = useCreditProductUnlocked(
    user?.id,
    GAME_HOST_UNLOCK_PRODUCT_ID,
  );
  const { loading: levyraatiHostUnlockLoading, unlocked: levyraatiHostUnlocked } = useCreditProductUnlocked(
    user?.id,
    LEVYRAATI_HOST_UNLOCK_PRODUCT_ID,
  );
  const isAdmin = user?.role === 'admin';

  const author = user?.displayName || user?.username || '';

  useEffect(() => {
    if (gameHostUnlockLoading || levyraatiHostUnlockLoading) return;
    if (!gameHostUnlocked && eventType === 'game') {
      setEventType('keskustelu');
      setGameType('riddle-fi');
      setLevyraatiPlaylist([]);
    } else if (!levyraatiHostUnlocked && eventType === 'levyraati') {
      setEventType('keskustelu');
      setLevyraatiPlaylist([]);
      setLevyraatiTrackUrl('');
      setLevyraatiResolveError(null);
      setLevyraatiResolveHint(null);
    }
  }, [
    gameHostUnlockLoading,
    levyraatiHostUnlockLoading,
    gameHostUnlocked,
    levyraatiHostUnlocked,
    eventType,
  ]);

  useEffect(() => {
    if (!isAdmin && eventType === 'movie') {
      setEventType('keskustelu');
      setMovieId('');
      setGameType('riddle-fi');
    }
  }, [isAdmin, eventType]);

  useEffect(() => {
    if (!eventDate) {
      setEventsOnDay([]);
      return;
    }
    fetch(`${API}/api/events?date=${eventDate}`)
      .then((res) => res.json())
      .then((data) => setEventsOnDay(Array.isArray(data) ? data : []))
      .catch(() => setEventsOnDay([]));
  }, [eventDate]);

  useEffect(() => {
    fetch(`${API}/api/movies`)
      .then((res) => res.json())
      .then((data) => {
        setMovies(Array.isArray(data) ? data : []);
      })
      .catch(() => setMovies([]));
  }, []);

  const durationMinutes = durationHours * 60;
  const slotFree = eventDate && eventTime && isSpecificSlotFree(eventsOnDay, eventTime, durationMinutes, roomNumber);
  const notInPast = isDateAndTimeInFuture(eventDate, eventTime, durationMinutes);
  const isYouTubeUrl = (url) => {
    const t = (url || '').trim();
    if (!t) return true;
    return /youtu\.be\//i.test(t) || /youtube\.com\//i.test(t);
  };
  const embedUrlInvalid =
    eventType === 'keskustelu' && embedUrl.trim() !== '' && !isYouTubeUrl(embedUrl);
  const slotSeconds = durationMinutes * 60;
  const levyraatiDurationRoundedUp = (sec) => Math.ceil((Number(sec) || 0) / 60) * 60;
  const levyraatiTotalSeconds = levyraatiPlaylist.reduce((s, t) => s + levyraatiDurationRoundedUp(t.duration_seconds), 0);
  const levyraatiCanAdd = eventType === 'levyraati' && levyraatiTrackUrl.trim() && levyraatiTotalSeconds < slotSeconds;
  const formValid =
    eventName.trim() !== '' &&
    eventDate !== '' &&
    eventTime !== '' &&
    slotFree &&
    notInPast &&
    !embedUrlInvalid &&
    (eventType !== 'levyraati' || levyraatiPlaylist.length > 0) &&
    (eventType !== 'livestream' || (livestreamType && livestreamCategory));

  const handleEventTypeChange = (e) => {
    const value = e.target.value;
    setEventType(value);
    if (value === 'keskustelu') {
      setGameType('riddle-fi');
      setLevyraatiPlaylist([]);
    } else if (value === 'game') {
      setEmbedUrl('');
      setLevyraatiPlaylist([]);
    } else if (value === 'levyraati') {
      setEmbedUrl('');
      setLevyraatiPlaylist([]);
      setLevyraatiTrackUrl('');
      setLevyraatiResolveError(null);
      setLevyraatiResolveHint(null);
    } else if (value === 'movie') {
      setEmbedUrl('');
      setGameType('none');
    } else if (value === 'livestream') {
      setEmbedUrl('');
      setGameType('none');
      setLevyraatiPlaylist([]);
      setLivestreamType('music');
      setLivestreamCategory('pop');
    }
  };

  const handleLevyraatiResolve = () => {
    const url = levyraatiTrackUrl.trim();
    if (!url || levyraatiResolving) return;
    if (levyraatiTotalSeconds >= slotSeconds) return;
    setLevyraatiResolveError(null);
    setLevyraatiResolveHint(null);
    setLevyraatiResolving(true);
    fetch(`${API}/api/levyraati/resolve-track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setLevyraatiResolveError(data.error);
          return;
        }
        const dur = Number(data.duration_seconds) || 0;
        const durRounded = levyraatiDurationRoundedUp(dur);
        if (levyraatiTotalSeconds + durRounded > slotSeconds) {
          setLevyraatiResolveError('Listan kokonaiskesto ylittäisi slotin.');
          return;
        }
        setLevyraatiPlaylist((prev) => [
          ...prev,
          {
            position: prev.length,
            source: data.source || '',
            url,
            track_author: data.track_author || '',
            track_name: data.track_name || '',
            duration_seconds: dur,
          },
        ]);
        setLevyraatiTrackUrl('');
        setLevyraatiResolveHint(data.duration_hint || null);
      })
      .catch(() => setLevyraatiResolveError('URL:n tulkinta epäonnistui.'))
      .finally(() => setLevyraatiResolving(false));
  };

  const removeLevyraatiTrack = (index) => {
    setLevyraatiPlaylist((prev) => prev.filter((_, i) => i !== index).map((t, i) => ({ ...t, position: i })));
  };

  const handleLevyraatiAdd10Random = () => {
    if (levyraatiRandomLoading || eventType !== 'levyraati') return;
    if (levyraatiTotalSeconds >= slotSeconds) return;
    setLevyraatiResolveError(null);
    setLevyraatiRandomLoading(true);
    fetch(`${API}/api/tracks/feeling-lucky?limit=10`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setLevyraatiResolveError(data.error);
          return;
        }
        const list = Array.isArray(data.tracks) ? data.tracks : [];
        setLevyraatiPlaylist((prev) => {
          let total = prev.reduce((s, t) => s + levyraatiDurationRoundedUp(t.duration_seconds), 0);
          const added = [];
          for (const t of list) {
            const dur = Number(t.duration_seconds) || 0;
            const durRounded = levyraatiDurationRoundedUp(dur);
            if (total + durRounded > slotSeconds) break;
            total += durRounded;
            added.push({
              position: prev.length + added.length,
              source: 'audius',
              url: t.url || '',
              track_author: t.track_author || '',
              track_name: t.track_name || '',
              duration_seconds: dur,
            });
          }
          return [...prev, ...added];
        });
      })
      .catch(() => setLevyraatiResolveError('Satunnaisten biisien haku epäonnistui.'))
      .finally(() => setLevyraatiRandomLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formValid || saving) return;
    setError(null);
    setSaving(true);
    fetch(`${API}/api/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: eventName.trim(),
        author,
        createdByUserId: user?.id ?? undefined,
        eventDate,
        time: eventTime,
        durationHours,
        embedUrl: eventType === 'keskustelu' ? embedUrl.trim() : '',
        description: description.trim() || null,
        enableQA,
        eventType,
        roomNumber,
        ...(eventType === 'livestream' && { livestreamType, livestreamCategory }),
        movieId: eventType === 'movie' && movieId ? movieId : undefined,
        gameType: eventType === 'game' && gameType ? gameType : 'none',
        ...(eventType === 'levyraati' && { levyraati_playlist: levyraatiPlaylist }),
      }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((err) => { throw new Error(err.error || 'Failed to create event'); });
        return res.json();
      })
      .then((updatedEvents) => {
        setSaving(false);
        if (typeof onSaved === 'function') onSaved(updatedEvents);
      })
      .catch((err) => {
        setError(err.message || 'Failed to save');
        setSaving(false);
      });
  };

  const formatFinDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString( { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="CreateEvent-view App-block">
      <h2>Luo uusi tapahtuma</h2>
      <form className="CreateEvent-form" onSubmit={handleSubmit}>
        <label htmlFor="eventName" className="CreateEvent-field-required">
          Tapahtuman nimi
          </label>
          <input
          id="eventName"
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder="Tapahtuman nimi"
            required
          />
        <label htmlFor="author" className="CreateEvent-field-required CreateEvent-field--readonly">
        Järjestäjä
        </label>
        <input id="author" type="text" value={author} readOnly disabled />
        <div className="CreateEvent-field CreateEvent-duration-row">
          <span className="CreateEvent-duration-label CreateEvent-field-required">Kesto: {durationHours} t</span>
          <input
            type="range"
            min={1}
            max={3}
            step={1}
            value={durationHours}
            onChange={(e) => setDurationHours(Number(e.target.value))}
            className="CreateEvent-slider"
            aria-label="Kesto tunteina"
          />

        </div>
        <label htmlFor="roomNumber" className="CreateEvent-field-required">
          Virtuaalihuone
        </label>
        <select
          id="roomNumber"
          value={roomNumber}
          onChange={(e) => setRoomNumber(Number(e.target.value))}
          className="CreateEvent-time-select"
          required
          aria-label="Virtuaalihuone"
        >
          {Array.from({ length: VIRTUAL_ROOM_COUNT }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {t('event.room')} {n}
            </option>
          ))}
        </select>
        <label className="CreateEvent-field-required">
          Päivämäärä
          </label>

          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            min={todayStr()}
            required
          />

        <label htmlFor="eventTime" className="CreateEvent-field-required">
          KLO
          </label>
          <select
            id="eventTime"
            value={eventTime}
            onChange={(e) => setEventTime(e.target.value)}
            required
            className="CreateEvent-time-select"
          >
            {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22].map((h) => {
              const value = `${String(h).padStart(2, '0')}:00`;
              return (
                <option key={value} value={value}>
                  {value}
                </option>
              );
            })}
          </select>
        {eventDate && (
          <div className="CreateEvent-day-calendar">
            <h4>Tapahtumat {formatFinDate(eventDate)}</h4>
            {eventsOnDay.length === 0 ? (
              <p className="CreateEvent-day-empty">Ei tapahtumia vielä. Tähän mahtuu.</p>
            ) : (
              <ul className="CreateEvent-day-list">
                {eventsOnDay
                  .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                  .map((ev) => (
                    <li key={ev.id}>
                      {ev.time} — <EventTypeTitle event={ev} /> ({ev.minutes || 0} min)
                    </li>
                  ))}
              </ul>
            )}
            {eventDate && eventTime && !slotFree && (
              <p className="CreateEvent-day-full">Valitsemasi aikaväli ei ole mahdollinen.</p>
            )}
            {eventDate && eventTime && slotFree && !notInPast && (
              <p className="CreateEvent-day-full">Valittu aikaväli on jo mennyt.</p>
            )}
          </div>
        )}
        <label htmlFor="eventType" className="CreateEvent-section-heading CreateEvent-field-required">
          Tapahtuman tyyppi
        </label>
        <select
          id="eventType"
          value={eventType}
          onChange={handleEventTypeChange}
          className="CreateEvent-time-select"
          required
          aria-label="Tapahtuman tyyppi"
        >
          <option value="keskustelu">keskustelu</option>
          <option value="game" disabled={gameHostUnlockLoading || !gameHostUnlocked}>
            peli
          </option>
          <option value="levyraati" disabled={levyraatiHostUnlockLoading || !levyraatiHostUnlocked}>
            levyraati
          </option>
          {isAdmin ? <option value="movie">elokuva</option> : null}
          <option value="livestream">livestriimi</option>
        </select>
        {eventType === 'livestream' && (
          <>
            <label htmlFor="livestreamType" className="CreateEvent-field">
              Livestriimin tyyppi
            </label>
            <select
              id="livestreamType"
              value={livestreamType}
              onChange={(e) => {
                const t = e.target.value;
                setLivestreamType(t);
                setLivestreamCategory(t === 'talk' ? 'en' : 'pop');
              }}
              className="CreateEvent-select"
            >
              <option value="music">Musiikki</option>
              <option value="talk">Puhe</option>
            </select>
            <label htmlFor="livestreamCategory" className="CreateEvent-field">
              {livestreamType === 'music' ? 'Genre' : 'Kieli'}
            </label>
            <select
              id="livestreamCategory"
              value={livestreamCategory}
              onChange={(e) => setLivestreamCategory(e.target.value)}
              className="CreateEvent-select"
            >
              {(livestreamType === 'music' ? LIVESTREAM_GENRES : LIVESTREAM_LANGUAGES).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </>
        )}
        {eventType === 'movie' && (
          <>
            <label htmlFor="movieId" className="CreateEvent-field">
              Elokuva
            </label>
            <select
              id="movieId"
              value={movieId}
              onChange={(e) => setMovieId(e.target.value)}
              className="CreateEvent-select"
            >
              <option value="">Valitse elokuva</option>
              {movies.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.movieName || m.movie_name || m.id}
                </option>
              ))}
            </select>
          </>
        )}
        {eventType === 'levyraati' && (
          <>
            <div className="CreateEvent-field CreateEvent-levyraati-list">
              <label className="CreateEvent-field">Levyraati-lista (Audius)</label>
              <p className="CreateEvent-levyraati-hint">Kesto: {Math.floor(levyraatiTotalSeconds / 60)} min / {durationMinutes} min</p>
              <div className="CreateEvent-levyraati-add">
                <input
                  type="url"
                  value={levyraatiTrackUrl}
                  onChange={(e) => { setLevyraatiTrackUrl(e.target.value); setLevyraatiResolveError(null); }}
                  placeholder="https://audius.co/..."
                  className="CreateEvent-levyraati-url"
                />
                <button
                  type="button"
                  className="Button"
                  disabled={!levyraatiCanAdd || levyraatiResolving}
                  onClick={handleLevyraatiResolve}
                >
                  {levyraatiResolving ? 'Haetaan…' : 'Lisää'}
                </button>
              </div>
              {levyraatiResolveError && <p className="CreateEvent-error">{levyraatiResolveError}</p>}
              {levyraatiResolveHint && !levyraatiResolveError && <p className="CreateEvent-levyraati-hint" style={{ marginTop: '0.25rem', color: 'var(--color-text-muted, #666)', fontSize: '0.9rem' }}>{levyraatiResolveHint}</p>}
              <div className="CreateEvent-levyraati-add" style={{ marginTop: '0.5rem' }}>
                <button
                  type="button"
                  className="Button Button-secondary"
                  disabled={levyraatiRandomLoading || levyraatiTotalSeconds >= slotSeconds}
                  onClick={handleLevyraatiAdd10Random}
                >
                  {levyraatiRandomLoading ? 'Haetaan…' : 'Lisää 10 random biisiä'}
                </button>
              </div>
              {levyraatiPlaylist.length > 0 && (
                <ul className="CreateEvent-levyraati-tracks">
                  {levyraatiPlaylist.map((t, i) => (
                    <li key={i} className="CreateEvent-levyraati-track">
                      <span>{t.track_author} – {t.track_name}</span>
                      <span className="CreateEvent-levyraati-dur">{t.duration_seconds ? `${Math.floor(t.duration_seconds / 60)}:${String(t.duration_seconds % 60).padStart(2, '0')}` : '—'}</span>
                      <button type="button" className="Button Button-secondary CreateEvent-levyraati-remove" onClick={() => removeLevyraatiTrack(i)} aria-label="Poista">×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
        {eventType === 'keskustelu' && (
          <>
            <label htmlFor="embedUrl" className="CreateEvent-field">
              Video
            </label>
            <input
              id="embedUrl"
              type="url"
              value={embedUrl}
              onChange={(e) => setEmbedUrl(e.target.value)}
              placeholder="https://..."
            />
            {embedUrlInvalid && (
              <p className="CreateEvent-error CreateEvent-error--video">
                Vain Youtube-videot mahdollisia tällä hetkellä..
              </p>
            )}
          </>
        )}

        {eventType === 'game' && (
          <>
            <label htmlFor="gameType" className="CreateEvent-field">
              Peli tapahtumassa
            </label>
            <select
              id="gameType"
              value={gameType}
              onChange={(e) => setGameType(e.target.value)}
              className="CreateEvent-select"
            >
              <option value="riddle-fi">Arvoituspeli</option>
              <option value="trivia-fi">Tietovisa</option>
              <option value="emoji-fi">Emoji-arvausta</option>
              <option value="abcd-fi">ABCD-tietovisa</option>
            </select>
          </>
        )}

        <label htmlFor="eventDescription" className="CreateEvent-field">
          Tapahtuman Kuvaus
        </label>
        <MarkdownEditor
          id="eventDescription"
          value={description}
          onChange={setDescription}
        />
        <label htmlFor="enableQA" className="CreateEvent-field">
        Q&A-osio
        </label>
          <input
            id="enableQA"
            type="checkbox"
            checked={enableQA}
            onChange={(e) => setEnableQA(e.target.checked)}
            className="CreateEvent-checkbox"
          />

        {error && <p className="CreateEvent-error">{error}</p>}
        <div className="CreateEvent-actions">
          <button type="button" className="Button CreateEvent-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="Button CreateEvent-save" disabled={!formValid || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
